import { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { Phone, Mail, Check } from 'lucide-react'
import { contactCTAs } from './config'
import type { ChatMessageProps } from '@/types/chatbot'

/**
 * Parses markdown-style bold text (**text**) and returns React elements
 */
function parseMarkdownBold(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2)
      return <strong key={index} className="font-semibold">{boldText}</strong>
    }
    return part
  })
}

const ctaIcons: Record<string, React.ReactNode> = {
  phone: <Phone className="w-3 h-3" />,
  email: <Mail className="w-3 h-3" />,
}

function CopyButton({ id, label, value, copiedLabel }: { id: string; label: string; value: string; copiedLabel: string }) {
  const [copied, setCopied] = useState(false)

  const handleClick = () => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleClick}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] transition-colors"
      style={{
        color: copied ? '#10b981' : '#9ca3af',
      }}
      onMouseEnter={(e) => {
        if (!copied) {
          e.currentTarget.style.color = '#00a3cc'
          e.currentTarget.style.backgroundColor = 'rgba(0, 163, 204, 0.05)'
        }
      }}
      onMouseLeave={(e) => {
        if (!copied) {
          e.currentTarget.style.color = '#9ca3af'
          e.currentTarget.style.backgroundColor = 'transparent'
        }
      }}
    >
      {copied ? <Check className="w-3 h-3" /> : ctaIcons[id]}
      <span>{copied ? copiedLabel : label}</span>
    </button>
  )
}

export function ChatMessage({ message, isTyping = false }: ChatMessageProps) {
  const { t } = useTranslation('common')
  const isUser = message.role === 'user'

  const formattedContent = useMemo(() => {
    if (!message.content) return null
    return parseMarkdownBold(message.content)
  }, [message.content])

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-2.5',
          isUser
            ? 'bg-gray-900 text-white rounded-br-sm'
            : 'bg-white text-gray-900 rounded-bl-sm shadow-sm'
        )}
      >
        {isTyping ? (
          <div className="flex gap-1.5 py-1 px-1">
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap leading-relaxed">
            {formattedContent}
          </p>
        )}
      </div>

      {/* Contact CTAs - only for assistant messages, not typing */}
      {!isUser && !isTyping && message.content && (
        <div className="flex items-center gap-1 mt-1.5 ml-1">
          {contactCTAs.map((cta) => (
            <CopyButton
              key={cta.id}
              id={cta.id}
              label={t(cta.labelKey)}
              value={cta.value}
              copiedLabel={t('chatbot.cta.copied')}
            />
          ))}
        </div>
      )}
    </div>
  )
}
